<script>
  import { onMount } from 'svelte';
  import XLSX from 'xlsx/dist/xlsx.full.min.js';
  import ComboInput from './ComboInput.svelte';
  import {lastModifiedStore} from './stores.js';

  let lastModified = new Date();
  let data = [];
  let filteredData = [];
  
  let filters = {
    satName: { list: [], value: '' },
    ntcId: { list: [], value: '' },
    adm: { list: [], value: '' },
    org: { list: [], value: '' },
    ssn: { list: [], value: '' },
    rev: { list: [], value: '' },
    ificNo: { list: [], value: '' }
  };

  let sortColumn = '';
  let sortOrder = 1;
  let itemRange = { start: 0, end: 0, total: 0 };

  let paginatedData = [];
  let currentPage = 1;
  let itemsPerPage = 50;
  $: totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // define a global boolean variable to toggle the filter, whose initial value is true
  let toggleFilter = true;

  function downloadExcel() {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData);
    XLSX.utils.book_append_sheet(wb, ws, "EPFD Data");
    XLSX.writeFile(wb, "epfd_data.xlsx");
  }

  // Fetch initial data
  onMount(async () => {
    try {
      // to clean page history, but not work....
      const timestamp = new Date().getTime();
      const response = await fetch(`./epfd_webtable.json?t=${timestamp}`);

      //const response = await fetch('./epfd_webtable.json');
      
      if (!response.ok) {
        throw new Error('Fetch data response was not ok');
      } else {
        lastModified = response.headers.get('Last-Modified');
        //console.log ('json file time1: ', lastModified);
        lastModifiedStore.set(lastModified);

        data = await response.json();
        data = data.map(item => ({
          ...item,
          IFIC_date: new Date(item.IFIC_date)
        }));

        updateFilterLists(false);
        filterAndSortData();
      }     
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  });

  //* To display the option list for each ComboInput component *//
  function updateFilterLists(isSelected) {
    // First, filter the data based on current input or selected
    let currentFilteredData = data.filter(item =>
      Object.entries(filters).every(([key, filter]) => {
        if (!filter.value) return true; // If no value input or selected, don't filter
        let field = getFieldName(key);
        if (isSelected){
          if (key === 'ssn' && filter.value === 'Notification (all parts)') 
            return String(item[field]).toLowerCase().startsWith('part');
          return String(item[field]).toLowerCase()=== String(filter.value).toLowerCase();
        }else{
          return String(item[field]).toLowerCase().startsWith(String(filter.value).toLowerCase()); 
        }             
      })
    );

    // Then, sort the lists for each filter     
    Object.keys(filters).forEach(key => {
      let field = getFieldName(key);
      filters[key].list = [...new Set(currentFilteredData.map(item => String(item[field])))].sort();            
      //console.log(`Updated ${key} list:`, filters[key].list);
    });
  }
  
  //* To update displayed data in the table, by filtering and sorting *//
  function filterAndSortData() {   
    filteredData = data.filter(item =>
      Object.entries(filters).every(([key, filter]) => {
        // console.log(`filterAndSortData... ${key}'|| selected value:`, filter.value);
        if (!filter.value){
          return true; // If no value input or selected, then don't filter          
        } 
        else {
          let field = getFieldName(key);

          if (key === 'ssn' && filter.value === 'Notification (all parts)') 
            return String(item[field]).toLowerCase().startsWith('part');
          
          return String(item[field]).toLowerCase()=== String(filter.value).toLowerCase();
        }         
      })
    );

    // For CR/C show only records with results
    if (toggleFilter) {
      filteredData = filteredData.filter(item => {
        return !(item.ssn_ref === 'CR/C' && item.url_files_result==="" && item.url_files_summary==="");
      });
    }

    if (sortColumn) {
      filteredData.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) return -1 * sortOrder;
        if (a[sortColumn] > b[sortColumn]) return 1 * sortOrder;
        return 0;
      });     
    } 
    currentPage = 1;
    updatePaginatedData();
  }

  //* when there is any text entered in the Input box *//
  function handleFilterChange(key, value) {
    //console.log(`handleFilterChange Updated ${key} list:`);
    filters[key].value = value;
    updateFilterLists(false);
    filterAndSortData();
  }

  //* when there is any value selected from the option list *//
  function handleFilterSelect(key, selectedValue) {    
    filters[key].value = selectedValue;
    //console.log(`handleFilterSelect get ${key} selected value:`, selectedValue);   
    updateFilterLists(true);
    filterAndSortData();
  }

  function handleToggleChange() {    
    // the boolean value of toggleFiltered is auto-changed with the toggle's switch event
    filterAndSortData();
  }

  function getFieldName(key) {
    switch(key) {
      case 'satName': return 'sat_name';
      case 'ntcId': return 'ntc_id';
      case 'adm': return 'adm';
      case 'org': return 'org';
      case 'ssn': return 'ssn_ref';
      case 'rev': return 'ssn_rev';
      case 'ificNo': return 'IFIC_no';
      default: return key;
    }
  }  

  function handleSort(column) {
    if (sortColumn === column) {
      sortOrder *= -1;
    } else {
      sortColumn = column;
      sortOrder = 1;
    }
    filterAndSortData();
  }

  function getSortIcon(column) {
    if (sortColumn !== column) return '⇵';
    return sortOrder === 1 ? '↑' : '↓';
  }

  function formatDate(date) {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  function updatePaginatedData() {
    const start = (currentPage - 1) * itemsPerPage;
    const end = Math.min(start + itemsPerPage, filteredData.length);    
    paginatedData = filteredData.slice(start, end);

    itemRange = {
      start: filteredData.length === 0 ? 0 : start + 1,
      end: end,
      total: filteredData.length
    };
  }

  function goToPage(page) {
    currentPage = page;
    updatePaginatedData();
  }

  function nextPage() {
    if (currentPage < totalPages) {
      currentPage++;
      updatePaginatedData();
    }
  }

  function prevPage() {
    if (currentPage > 1) {
      currentPage--;
      updatePaginatedData();
    }
  }
</script>

<main>    
  <div class="table-container">  

    <div class="above-table">
      <p class="results-info">
        Displaying {itemRange.start} to {itemRange.end} of {itemRange.total} results
      </p>
      <div class="pagination">
        <button on:click={prevPage} disabled={currentPage === 1}>Previous</button>
        {#each Array(totalPages) as _, i}
          <button 
            on:click={() => goToPage(i + 1)}
            class:active={currentPage === i + 1}
          >{i + 1}</button>
        {/each}
        <button on:click={nextPage} disabled={currentPage === totalPages}>Next</button>
      </div>
      <button class="download-btn" on:click={downloadExcel}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        Download
      </button>
    </div>

    <div class="toggle-container">
      <label for="toggle-switch">For CR/C show only records with results
        <div class="switch">
          <input type="checkbox" id="toggle-switch" bind:checked={toggleFilter} class="switch-input" on:change={handleToggleChange} />
          <span class="switch-slider"></span>
        </div>
      </label>
    </div>
    
    <table>
      <thead>
        <tr>
          <th>
            <span>Satellite Name</span>
            <span class="sort-icon" on:click={() => handleSort('sat_name')}>{getSortIcon('sat_name')}</span>
            <ComboInput
              options={filters.satName.list}
              bind:value={filters.satName.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('satName', e.detail)}
              on:select={e => handleFilterSelect('satName', e.detail)}
            />
          </th>
          <th>
            <span>Notice ID</span>
            <span class="sort-icon" on:click={() => handleSort('ntc_id')}>{getSortIcon('ntc_id')}</span>
            <ComboInput
              options={filters.ntcId.list}
              bind:value={filters.ntcId.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('ntcId', e.detail)}
              on:select={e => handleFilterSelect('ntcId', e.detail)}
            />
          </th>
          <th>
            <span>Administration</span>
            <span class="sort-icon" on:click={() => handleSort('adm')}>{getSortIcon('adm')}</span>
            <ComboInput
              options={filters.adm.list}
              bind:value={filters.adm.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('adm', e.detail)}
              on:select={e => handleFilterSelect('adm', e.detail)}
            />
          </th>
          <th>
            <span>IGSO</span>
            <span class="sort-icon" on:click={() => handleSort('org')}>{getSortIcon('org')}</span>
            <ComboInput
              options={filters.org.list}
              bind:value={filters.org.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('org', e.detail)}
              on:select={e => handleFilterSelect('org', e.detail)}
            />
          </th>
          <th>
            <span>Special Section / Parts</span>
            <span class="sort-icon" on:click={() => handleSort('ssn_ref')}>{getSortIcon('ssn_ref')}</span>
            <ComboInput
              options={[...filters.ssn.list, 'Notification (all parts)']}
              bind:value={filters.ssn.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('ssn', e.detail)}
              on:select={e => handleFilterSelect('ssn', e.detail)}
            />
          </th>
          <th>
            {#if filters.rev.value !== undefined}
            <span>Section number</span>
            <span class="sort-icon" on:click={() => handleSort('ssn_rev')}>{getSortIcon('ssn_rev')}</span>
            <ComboInput
              options={filters.rev.list}
              bind:value={filters.rev.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('rev', e.detail)}
              on:select={e => handleFilterSelect('rev', e.detail)}
            />
          {/if}
          </th>
          <th>
            <span>IFIC No</span>
            <span class="sort-icon" on:click={() => handleSort('IFIC_no')}>{getSortIcon('IFIC_no')}</span>
            <ComboInput
              options={filters.ificNo.list}
              bind:value={filters.ificNo.value}
              placeholder="Search..."
              on:input={e => handleFilterChange('ificNo', e.detail)}
              on:select={e => handleFilterSelect('ificNo', e.detail)}
            />
          </th>
          <th>IFIC Date</th>
          <th>Input</th>
          <th>Result</th>
          <th>Summary</th>
          <th>Remarks</th>
        </tr>
      </thead>
      <tbody>
         <!-- {#each filteredData as item} -->
        {#each paginatedData as item}
          <tr>
            <td>{item.sat_name}</td>
            <td>{item.ntc_id}</td>
            <td>{item.adm}</td>
            <td>{item.org || '  '}</td>
            <td>{item.ssn_ref}</td>
            <td>{item.ssn_rev || ''}</td>
            <td>{item.IFIC_no}</td>
            <td>{formatDate(item.IFIC_date)}</td>
            <td>
              {#if item.url_files_input}
                <a href={item.url_files_input} target="_blank">Input</a>               
              {/if}
            </td>
            <td>
              {#if item.url_files_result}
                <a href={item.url_files_result} target="_blank">Result</a>               
              {/if}
            </td>
            <td>
              {#if item.url_files_summary}
                <a href={item.url_files_summary} target="_blank">Summary</a>               
              {/if}
            </td>
            <td>{item.Remarks || ''}</td>
          </tr>
        {/each}
      </tbody>
    </table>    
  </div>  
</main>

<style>
  ::placeholder {
    font-size: 3px;
  }
  :global(*) {    
    font-family: "Segoe UI", sans-serif;
    font-size: 13px;
  }
  .table-container {    
    margin-top: 30px; /* Adjust this value to create space for the results info */
    font-family: "Calibri", serif;
    font-size: 10px;
    margin: auto;
    max-width: 98%;
    line-height: auto;
  }
  .above-table {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
  }
  .results-info {
    margin: 0;  
    font-style: italic;
    color: #666;
  }
  .download-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 12px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  .download-btn:hover {
    background-color: #45a049;
  }
  .download-btn svg {
    width: 16px;
    height: 16px;
  }
  .toggle-container {
    margin-bottom: 10px;
  }
  .toggle-container label {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
  }
  .switch-input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  .switch-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 20px;
  }
  .switch-slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
  .switch-input:checked + .switch-slider {
    background-color: #4CAF50;
  }
  .switch-input:checked + .switch-slider:before {
    transform: translateX(20px);
  }
  table {
    width: 100%;
    border-collapse: separate;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 5px;
    text-align: center;
  }
  th {
    background-color: #f2f2f2;
    padding: 10px 5px 40px 5px;
    vertical-align: auto;
    text-align: center;        
    position: relative;
    overflow: visible; /* Ensure dropdown is not cut off */
  }  
  th > :global(.combo-input) {
    position: relative;
    bottom: 5px;
    left: 5px;
    right: 5px;
  }
  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  th span {
    display: inline-block;
    margin-right: 5px;
  }
  .sort-icon {
    cursor:auto ;
    user-select: none;
    padding-left: 5px;
  }
  .sort-icon:hover {
    color: #007bff;
  }
  .pagination {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }
  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    border: 1px solid #ddd;
    background-color: #f8f8f8;
    cursor: pointer;
  }
  .pagination button.active {
    background-color: #007bff;
    color: white;
  }
  .pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
   /* Column width adjustments */
  th:nth-child(1), td:nth-child(1) { width: 8%; } /* Satellite Name */
  th:nth-child(2), td:nth-child(2) { width: 5%; } /* Notice ID */
  th:nth-child(3), td:nth-child(3) { width: 7%; } /* Administration */
  th:nth-child(4), td:nth-child(4) { width: 5%; } /* Orgnization */
  th:nth-child(5), td:nth-child(5) { width: 9%; } /* Special Section / Parts */
  th:nth-child(6), td:nth-child(6) { width: 7%; }  /* Section number */
  th:nth-child(7), td:nth-child(7) { width: 5%; }  /* IFIC No */
  th:nth-child(8), td:nth-child(8) { width: 5%; } /* IFIC Date */
  th:nth-child(9), td:nth-child(9) { width: 4%; }  /* Input */
  th:nth-child(10), td:nth-child(10) { width: 4%; }  /* Result */
  th:nth-child(11), td:nth-child(11) { width: 4%; }  /* Summary */
  th:nth-child(12), td:nth-child(12) { width: 20%; } /* Remarks */

</style>